import CacheOperations from '@/components/common/ed-board/ed-board-tools/ed-board-persistence/board-cache-operations';

export default class PersistentBoardDataUtils {

  /**
   * Map of last operation id for each user/boardGroup combination
   */
  private static readonly lastOperationMap = new Map<string, number>();


  public static saveOperationId(userName: string, boardGroup: string, operationId: string | number): void {
    if (operationId && userName && boardGroup) {
      // Race conditions could result in storing the operationId in the wrong order (an older operationId could
      // overwrite a newer one). To avoid this, we keep track of the last operationId in memory, so we only update the
      // localStorage item if the new operationId is greater than the last one.
      const lastKnownOpId = this.lastOperationMap.get(`${userName}_${boardGroup}`) || 0;
      if (Number(operationId) > lastKnownOpId) {
        this.lastOperationMap.set(`${userName}_${boardGroup}`, Number(operationId));
        localStorage.setItem(`${userName}_${boardGroup}.lastOperationId`, String(operationId));
      }

    }
  }

  public static async getAllAnnotations(userName: string, boardGroup: string, pages: number): Promise<any> {
    const cacheOperations = CacheOperations.getInstance();
    const allAnnotations = {};
    for (let i = 1; i <= pages; i++) {
      const pageAnnotations = await cacheOperations.getItem(`${userName}_${boardGroup}_${i}`);
      if (pageAnnotations) {
        allAnnotations[i] = pageAnnotations;
      }
    }
    return allAnnotations;
  }

  public static async boardHasAnnotations(userName: string, boardGroup: string, pages: number): Promise<boolean> {
    const cacheOperations = CacheOperations.getInstance();
    for (let i = 1; i <= pages; i++) {
      const pageAnnotations = await cacheOperations.getItem(`${userName}_${boardGroup}_${i}`);
      if (pageAnnotations) {
        return true
      }
    }
    return false;
  }

  public static clearLastOpId(userName: string, boardGroupHash: string): void {
    localStorage.removeItem(`${userName}_${boardGroupHash}.lastOperationId`);
    this.lastOperationMap.delete(`${userName}_${boardGroupHash}`);
  }

  public static clearUnsaved(userName: string, boardGroupHash: string): void {
    localStorage.removeItem(`${userName}_${boardGroupHash}.isUnsaved`);
  }

  public static getLocalLastOpId(userName: string, boardGroupHash: string): number {
    const item = localStorage.getItem(`${userName}_${boardGroupHash}.lastOperationId`);
    return Number(item) || 0;
  }

  /**
   * Checks if there are unsaved changes for a board. Beware that this method has a side effect: if there are unsaved
   * changes but no offline annotations, it will clear the unsaved flag.
   */
  public static async boardHasUnsavedAnnotations(userName: string, boardGroupHash: string, totalPages: number): Promise<boolean> {
    const areThereUnsavedChanges = this.isBoardUnsaved(userName, boardGroupHash);
    const areThereOfflineAnnotations = await this.boardHasAnnotations(userName, boardGroupHash, totalPages);

    if (areThereUnsavedChanges && !areThereOfflineAnnotations) {
      this.clearUnsaved(userName, boardGroupHash);
      return false;
    }
    return areThereUnsavedChanges;
  }

  public static isBoardUnsaved(userName: string, boardGroupHash: string): boolean {
    return Boolean(localStorage.getItem(`${userName}_${boardGroupHash}.isUnsaved`))

  }

}
